import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateStandardInput, Paginated, Standard, UpdateStandardInput } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  Table,
  Textarea,
  useToast,
} from '../../components/ui.js';

type Draft = {
  code: string;
  name: string;
  issuingBody: string;
  description: string;
  supportsSoa: boolean;
};
const EMPTY: Draft = { code: '', name: '', issuingBody: '', description: '', supportsSoa: false };

/**
 * The Statement of Applicability is an ISO/IEC 27001 artefact (clause 6.1.3 d),
 * so it is off unless an admin switches it on for a standard that has a control
 * set to declare applicability over.
 */
const SOA_HINT =
  'Offer a Statement of Applicability for this standard. Suitable for standards with a control set to declare applicability over, such as ISO 27001.';

const STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'neutral'> = {
  active: 'success',
  draft: 'info',
  deprecated: 'warning',
  archived: 'neutral',
};

export function ManageStandardsPage() {
  const { me } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isSystemAdmin = me?.user.isSystemAdmin ?? false;

  const [editing, setEditing] = useState<Standard | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);

  const standards = useQuery({
    queryKey: ['standards-admin'],
    queryFn: () => apiFetch<Paginated<Standard>>('/standards', { query: { limit: 200 } }),
    enabled: isSystemAdmin,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['standards-admin'] });
  const onError = (err: unknown) =>
    toast(err instanceof ApiRequestError ? err.message : 'Something went wrong', 'error');

  const create = useMutation({
    mutationFn: () => {
      const body: CreateStandardInput = {
        code: draft.code.trim().toLowerCase(),
        name: draft.name.trim(),
        issuingBody: draft.issuingBody.trim(),
        description: draft.description.trim(),
        supportsSoa: draft.supportsSoa,
      };
      return apiFetch<Standard>('/standards', { method: 'POST', body });
    },
    onSuccess: () => {
      toast('Standard created (as draft).', 'success');
      setCreating(false);
      setDraft(EMPTY);
      invalidate();
    },
    onError,
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateStandardInput }) =>
      apiFetch<Standard>(`/standards/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      toast('Standard updated.', 'success');
      setEditing(null);
      invalidate();
    },
    onError,
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: true }>(`/standards/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Standard deleted.', 'success');
      invalidate();
    },
    onError,
  });

  if (!isSystemAdmin) {
    return <EmptyState title="System administrators only" message="Platform standards management." />;
  }
  if (standards.isLoading) return <LoadingPane label="Loading standards" />;
  const items = standards.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Manage standards"
        subtitle="Create, edit, archive and delete the standards catalogue. Import bundles from the catalogue page."
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to="/standards">
              <Button variant="secondary">Import bundle</Button>
            </Link>
            <Button onClick={() => { setDraft(EMPTY); setCreating(true); }}>New standard</Button>
          </div>
        }
      />

      {items.length === 0 ? (
        <EmptyState
          title="No standards yet"
          message="Create one, or import a bundle from the standards catalogue."
          action={<Button onClick={() => setCreating(true)}>New standard</Button>}
        />
      ) : (
        <Card title={`${items.length} standards`}>
          <Table
            head={
              <>
                <th>Code</th>
                <th>Name</th>
                <th>Issuing body</th>
                <th>Status</th>
                <th />
              </>
            }
          >
            {items.map((s) => (
              <tr key={s.id}>
                <td>
                  <code>{s.code}</code>
                </td>
                <td style={{ fontWeight: 600 }}>
                  {s.currentVersionId ? (
                    <Link to={`/standards/versions/${s.currentVersionId}`}>{s.name}</Link>
                  ) : (
                    s.name
                  )}
                </td>
                <td>{s.issuingBody || '—'}</td>
                <td>
                  <Badge tone={STATUS_TONE[s.status] ?? 'neutral'}>{s.status}</Badge>{' '}
                  {s.supportsSoa && <Badge tone="info">SOA</Badge>}
                </td>
                <td>
                  <div className="record-row-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setEditing(s)}
                    >
                      Edit
                    </Button>
                    {s.status !== 'archived' ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => update.mutate({ id: s.id, body: { status: 'archived' } })}
                      >
                        Archive
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => update.mutate({ id: s.id, body: { status: 'draft' } })}
                      >
                        Restore
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="danger"
                      loading={remove.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete "${s.name}" and all its versions permanently? This is blocked if any assessment uses it — archive instead in that case.`,
                          )
                        ) {
                          remove.mutate(s.id);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      {/* Create */}
      <Dialog open={creating} onClose={() => setCreating(false)} title="New standard">
        <Field label="Code" hint="Lowercase, e.g. iso-27001 (used in URLs and bundles)" required>
          {(props) => (
            <Input {...props} value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value })} />
          )}
        </Field>
        <Field label="Name" required>
          {(props) => (
            <Input {...props} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          )}
        </Field>
        <Field label="Issuing body">
          {(props) => (
            <Input
              {...props}
              value={draft.issuingBody}
              onChange={(e) => setDraft({ ...draft, issuingBody: e.target.value })}
            />
          )}
        </Field>
        <Field label="Description">
          {(props) => (
            <Textarea
              {...props}
              rows={3}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          )}
        </Field>
        <div className="field">
          <label className="field-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={draft.supportsSoa}
              onChange={(e) => setDraft({ ...draft, supportsSoa: e.target.checked })}
            />
            Statement of Applicability
          </label>
          <span className="field-hint">{SOA_HINT}</span>
        </div>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setCreating(false)}>
            Cancel
          </Button>
          <Button
            loading={create.isPending}
            disabled={draft.code.trim() === '' || draft.name.trim() === ''}
            onClick={() => create.mutate()}
          >
            Create standard
          </Button>
        </div>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} title={`Edit ${editing?.name ?? ''}`}>
        {editing && (
          <>
            <Field label="Name" required>
              {(props) => (
                <Input
                  {...props}
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              )}
            </Field>
            <Field label="Issuing body">
              {(props) => (
                <Input
                  {...props}
                  value={editing.issuingBody}
                  onChange={(e) => setEditing({ ...editing, issuingBody: e.target.value })}
                />
              )}
            </Field>
            <Field label="Description">
              {(props) => (
                <Textarea
                  {...props}
                  rows={3}
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                />
              )}
            </Field>
            <Field label="Status">
              {(props) => (
                <select
                  {...props}
                  className="select"
                  value={editing.status}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value as Standard['status'] })}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="deprecated">Deprecated</option>
                  <option value="archived">Archived</option>
                </select>
              )}
            </Field>
            <div className="field">
              <label
                className="field-label"
                style={{ display: 'flex', gap: 8, alignItems: 'center' }}
              >
                <input
                  type="checkbox"
                  checked={editing.supportsSoa}
                  onChange={(e) => setEditing({ ...editing, supportsSoa: e.target.checked })}
                />
                Statement of Applicability
              </label>
              <span className="field-hint">{SOA_HINT}</span>
            </div>
            <div className="dialog-actions">
              <Button variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button
                loading={update.isPending}
                onClick={() =>
                  update.mutate({
                    id: editing.id,
                    body: {
                      name: editing.name,
                      issuingBody: editing.issuingBody,
                      description: editing.description,
                      status: editing.status,
                      supportsSoa: editing.supportsSoa,
                    },
                  })
                }
              >
                Save changes
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
}
